import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import moment from 'moment';

import useFetchWrapper, { HttpResponse } from '../../Helpers/useFetchWrapper';

import {
	CreateEventRequest,
	FormData,
	HookProps,
	EventResponse,
	NetworkResponse,
	Participant,
	UpdateEventRequest,
} from './ScheduleEvent.model';
import { useAuthorizationDataContext } from '../../Helpers/AuthorizationDataProvider';

const useScheduleEvent = ({ eventId, userId, onClose }: HookProps) => {
	const [events, setEvents] = useState<any[]>([]);
	const [initialListOfParticipants, setInitialListOfParticipants] = useState<string[]>([]);
	const [network, setNetwork] = useState<NetworkResponse[]>([]);
	const [participants, setParticipants] = useState<NetworkResponse[]>([]);
	const [meeting, setMeeting] = useState<any>();

	const defaultValues: any = {
		date: eventId ? moment(meeting?.startTime) : null,
		description: eventId ? meeting?.description : '',
		duration: '',
		endTime: eventId ? moment(meeting?.endTime) : null,
		startTime: eventId ? moment(meeting?.startTime) : null,
		title: eventId ? meeting?.title : '',
	};

	const { control, formState: { errors }, handleSubmit, setValue, watch, reset } = useForm({ defaultValues });

	const { get, put, post } = useFetchWrapper();

	const { isAuthorized } = useAuthorizationDataContext();

	const watchDate = watch('date');
	const watchStartTime: any = watch('startTime');
	const watchEndTime: any = watch('endTime');

	useEffect(() => {
		getNetwork();
	}, []);

	useEffect(() => {
		eventId && getMeeting();
	}, [eventId]);

	useEffect(() => {
		reset(defaultValues);
		meeting && setTimeout(() => setValue('endTime', moment(meeting.endTime)), 0); // Update endTime after the startTime watcher is run
	}, [meeting])

	useEffect(() => {
		watchDate && getScheduledMeetings();
	}, [watchDate]);

	useEffect(() => {

		if (watchStartTime && watchEndTime) {
			const diff = Math.ceil(watchEndTime.diff(watchStartTime));
			const minutes = Math.floor((diff / (1000 * 60)) % 60);
			const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);

			setValue('duration', `${hours}:${minutes < 10 ? `0${minutes}` : minutes} h`);
		}
	}, [watchEndTime]);

	useEffect(() => {
		watchStartTime && setValue('endTime', moment(watchStartTime).add(1, 'hour'));
	}, [watchStartTime]);

	const getNetwork = async () => {
		const response: HttpResponse<NetworkResponse[]> = await get(`/user/profile/network/${userId}?page=1&size=9999`);
		response.parsedBody && setNetwork(response.parsedBody);
	}

	const getMeeting = async () => {
		const response: HttpResponse<EventResponse> = await get(`/calendar/events/${eventId}`);

		if (response.parsedBody) {
			const { invitations } = response.parsedBody;

			setMeeting(response.parsedBody);
			setParticipants(invitations.map(invitation => invitation.guest));
			setNetwork(network.reduce((acc: NetworkResponse[], curr) => {
				if (!invitations.find(item => item.guest.userId === curr.userId)) {
					acc = [...acc, curr]
				}
				return acc;
			}, []));

			setInitialListOfParticipants(invitations.map(invitation => invitation.guest.userId));
		}
	}

	const getScheduledMeetings = async () => {
		const startDay = moment(watchDate).startOf('day').toISOString();
		const endDay = moment(watchDate).endOf('day').toISOString();
		const response: HttpResponse<any[]> = await get(`/calendar/events?from=${startDay}&to=${endDay}`);

		response.parsedBody && setEvents(response.parsedBody);
	}

	const addParticipant = (participant: Participant) => {
		// Remove person from network and add it to participants
		setNetwork(network.filter((item) => item.userId !== participant.userId));
		setParticipants([
			...participants,
			participant,
		]);
	}

	const onCreateEvent = async (data: FormData) => {
		const { description, date, endTime, startTime, title } = data;
		const callEndDate = moment(date);
		const callStartDate = moment(date);

		callEndDate.set('hour', endTime.get('hour'));
		callEndDate.set('minute', endTime.get('minute'));
		callStartDate.set('hour', startTime.get('hour'));
		callStartDate.set('minute', startTime.get('minute'));

		const body: CreateEventRequest = {
			description,
			endTime: callEndDate.toISOString(true),
			guests: participants.map(item => item.userId),
			startTime: callStartDate.toISOString(true),
			title,
		}

		await post(`/calendar/events`, body);

		onClose();
	}

	const onUpdateEvent = async (data: FormData) => {
		const { description, date, endTime, startTime, title } = data;
		const callEndDate = moment(date);
		const callStartDate = moment(date);

		callEndDate.set('hour', endTime.get('hour'));
		callEndDate.set('minute', endTime.get('minute'));
		callStartDate.set('hour', startTime.get('hour'));
		callStartDate.set('minute', startTime.get('minute'));

		const removedGuests = initialListOfParticipants.reduce((acc: string[], curr) => {
			if (!participants.find(item => item.userId === curr)) {
				acc = [...acc, curr];
			}
			return acc;
		}, []);

		const addedGuests = participants.reduce((acc: string[], curr) => {
			if (!initialListOfParticipants.find(item => item === curr.userId)) {
				acc = [...acc, curr.userId];
			}
			return acc;
		}, []);

		const body: UpdateEventRequest = {
			addedGuests,
			description,
			endTime: callEndDate.toISOString(true),
			removedGuests,
			startTime: callStartDate.toISOString(true),
			title,
		}

		await put(`/calendar/events/${eventId}`, body);

		onClose();
	}

	const removeParticipant = (participant: Participant) => {
		// Remove person from participants and add it to network
		setParticipants(participants.filter((item) => item.userId !== participant.userId));
		setNetwork([
			...network,
			participant,
		]);
	}

	return {
		addParticipant,
		control,
		defaultValues,
		errors,
		handleSubmit,
		isAuthorized,
		onCreateEvent,
		onUpdateEvent,
		removeParticipant,
		state: {
			events,
			network,
			participants,
		},
		watchEndTime,
		watchStartTime,
	}
}

export default useScheduleEvent;